<template>
    <v-card class="payment-options" :variant="outlined ? 'outlined' : 'elevated'" data-cy="payment-methods">
        <v-card-title>{{ $t('payment') }}</v-card-title>
        <v-card-text class="px-1 px-md-3">
            <div
                v-if="!paymentMethods.length"
                class="px-1"
                style="white-space: pre-line"
                v-text="$t('no_payment_options')"
            />
            <v-radio-group v-else v-model="paymentMethodId" :disabled="disabled">
                <template v-for="paymentMethod in paymentMethods" :key="paymentMethod.id">
                    <v-radio :value="paymentMethod.id" @click="onRadioClick(paymentMethod.id)">
                        <template v-slot:label>
                            <span>
                                <span>{{ `${paymentMethod.name} ` }}</span>
                                <strong v-if="totalCost(paymentMethod) > 0">
                                    {{
                                        $filters.priceFormat(
                                            totalCost(paymentMethod),
                                            paymentMethod.totalAmount.currency
                                        )
                                    }}
                                </strong>
                                <span v-if="pspBankFeeAmount(paymentMethod) && isB2B">
                                    (<span
                                        v-html="
                                            $t('psp_bank_fee', {
                                                bookingAmount: totalCostWithoutPspBankFeePrintable(paymentMethod),
                                                bankFee: pspBankFeePrintable(paymentMethod),
                                            })
                                        "
                                    />)
                                </span>
                            </span>
                        </template>
                    </v-radio>
                    <payment-option-img :payment-method="paymentMethod.pspCode" class="ms-8" />
                    <div class="text-caption ms-8 mb-2" v-html="paymentMethod.description" />
                    <div
                        v-if="
                            paymentMethod.splitPaymentOptions &&
                            paymentMethod.splitPaymentOptions.length &&
                            paymentMethod.id === paymentMethodId
                        "
                        class="pl-8"
                    >
                        <v-select
                            v-model="numberOfPayments"
                            :items="paymentsCounterList"
                            :label="$t('number_of_payments')"
                            class="payments-counter"
                        />
                        {{ `${$t('amount_of_first_and_next_payments')}:` }}
                        <span>
                            {{
                                $filters.priceFormat(
                                    totalCost(paymentMethod) / numberOfPayments,
                                    paymentMethod.totalAmount.currency
                                )
                            }}
                        </span>
                    </div>
                </template>
            </v-radio-group>
        </v-card-text>
    </v-card>
</template>

<script lang="ts">
    import {Component, Emit, Prop, VModel, Vue, Watch} from 'vue-facing-decorator'
    import PaymentOptionImg from '~src/components/booking/PaymentOptionImg.src'
    import {priceFormat} from '@/utils/filters'

    @Component({
        components: {
            PaymentOptionImg,
        },
    })
    export class PaymentOptions extends Vue {
        @VModel() paymentMethodId
        @Prop({default: false, type: Boolean}) disabled
        @Prop({default: false, type: Boolean}) outlined
        @Prop() _numberOfPayments

        @Emit()
        changeNumberOfPayments(val) {
            return val
        }

        @Watch('paymentsCounterList')
        changePaymentsCounterList(val) {
            if (val) this.numberOfPayments = this.paymentsCounterList[0]
            else this.numberOfPayments = null
        }

        totalCost(paymentMethod) {
            if (this.paymentMethodId && this.numberOfPayments) {
                const index = paymentMethod.splitPaymentOptions.findIndex(
                    option => option.split('_')[0] === this.numberOfPayments
                )

                if (index >= 0) {
                    const total = paymentMethod.totalAmount.value
                    const percent = Number(paymentMethod.splitPaymentOptions[index].split('_')[1])

                    return total + (total * percent) / 100
                }
            }
            return paymentMethod.totalAmount.value
        }

        onRadioClick(val) {
            if (val === this.paymentMethodId) {
                this.paymentMethodId = null
            }
        }

        pspBankFeeAmount(paymentMethod) {
            return +paymentMethod.pspBankFeeAmount
        }

        pspBankFeePrintable(paymentMethod) {
            return this.pspBankFeeAmount(paymentMethod)
                ? priceFormat(this.pspBankFeeAmount(paymentMethod), paymentMethod.totalAmount.currency)
                : null
        }

        totalCostWithoutPspBankFeeAmount(paymentMethod) {
            return (this.totalCost(paymentMethod) * 100 - this.pspBankFeeAmount(paymentMethod) * 100) / 100
        }

        totalCostWithoutPspBankFeePrintable(paymentMethod) {
            return priceFormat(this.totalCostWithoutPspBankFeeAmount(paymentMethod), paymentMethod.totalAmount.currency)
        }

        get paymentsCounterList() {
            if (this.paymentMethodId) {
                const paymentMethod = this.paymentMethods.find(method => method.id === this.paymentMethodId)
                if (!paymentMethod) return []
                return paymentMethod.splitPaymentOptions.reduce((paymentsCounter, option) => {
                    paymentsCounter.push(option.split('_')[0])

                    return paymentsCounter
                }, [])
            }
            return []
        }

        get paymentMethods() {
            return persistentStore.paymentMethods.filter(paymentMethod => paymentMethod.book)
        }

        get isB2B() {
            return authStore.isB2B
        }

        get numberOfPayments() {
            return this._numberOfPayments
        }

        set numberOfPayments(val) {
            this.changeNumberOfPayments(val)
        }
    }

    export default PaymentOptions
</script>

<style scoped>
    .payments-counter {
        max-width: 200px;
    }
</style>
